import { useState, useContext } from 'react';

import { doc, updateDoc } from 'firebase/firestore';

import { db } from '../../firebase_config';

import { GoodCard } from '../MainPage/GoodsList/GoodCard/GoodCard';

import { ModalWindow } from '../../Components/ModalWindow';
import { ChevronDoubleLeft } from '../../Components/SvgComponents/ChevronDoubleLeft';
import { ChevronLeft } from '../../Components/SvgComponents/ChevronLeft';
import { ChevronRight } from '../../Components/SvgComponents/ChevronRight';
import { ChevronDoubleRight } from '../../Components/SvgComponents/ChevronDoubleRight';
import { useResize } from '../../Components/useResizeHook';

import { UserContext } from '../UserContext';

export const CartPage = () => {
  const { user, count, sum, setCount, setSum, cart, setCart } = useContext(UserContext);
  const [isModalOpened, setOpenModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const goodsPerPage = useResize();

  const handleClearChest = () => {
    const userRef = doc(db, 'users', user.uid);
    setCount(0);
    setSum(0);
    setCart([]);
    updateDoc(userRef, { cart: [], count: 0, sum: 0 });
    setOpenModal(false);
  };

  const sortedCart = cart.reduce(
    (accumulator, currentValue) => {
      if (accumulator[accumulator.length - 1].length === goodsPerPage) {
        accumulator.push([]);
      }

      accumulator[accumulator.length - 1].push(currentValue);
      return accumulator;
    },
    [[]]
  );

  const turnNextPage = () => {
    if (currentPage < sortedCart.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const turnPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const swipeStartPage = () => {
    setCurrentPage(1);
  };

  const swipeEndPage = () => {
    setCurrentPage(sortedCart.length);
  };

  return (
    <section className="relative min-h-screen w-full">
      <div className="bg-cover bg-center bg-store bg-no-repeat absolute top-0 right-0 bottom-0 left-0">
        <div className="relative w-full h-full flex justify-center items-center">
          <div className="absolute top-0 right-0 bottom-0 left-0 z-10 bg-shadowDark flex flex-col pt-10 md:pt-20 items-center">
            <div className="h-96 flex justify-center items-center z-20 flex-wrap gap-4 md:gap-7">
              {cart.length ? (
                sortedCart[currentPage - 1].map((good) => <GoodCard key={good.id} data={good} />)
              ) : (
                <h2 className="font-poppins font-normal text-whiteMagic text-xl md:text-3xl text-center leading-7 tracking-wider">
                  Your cart is empty
                </h2>
              )}
            </div>
            <div className="mt-4 md:mt-6 self-center flex gap-3 md:gap-6">
              <button
                className={[
                  'p-1 rounded-full h-10 w-10 flex justify-center items-center font-poppins leading-5 border hover:bg-none duration-400 hover:border-borderColor hover:text-borderColor',
                  currentPage === 1
                    ? 'bg-none border-borderColor text-borderColor'
                    : 'bg-gradient-to-r from-grStart to-grEnd font-poppins border-transparent text-font',
                ].join(' ')}
                onClick={swipeStartPage}>
                <ChevronDoubleLeft />
              </button>
              <button
                className={[
                  'p-1 rounded-full h-10 w-10 flex justify-center items-center font-poppins leading-5 border hover:bg-none duration-400 hover:border-borderColor hover:text-borderColor',
                  currentPage === 1
                    ? 'bg-none border-borderColor text-borderColor'
                    : 'bg-gradient-to-r from-grStart to-grEnd font-poppins border-transparent text-font',
                ].join(' ')}
                onClick={turnPreviousPage}>
                <ChevronLeft />
              </button>
              <div className="p-1 rounded-full h-10 w-10 flex justify-center items-center bg-transparent font-poppins leading-5 border border-borderColor text-font">
                {currentPage}
              </div>
              <button
                className={[
                  'p-1 rounded-full h-10 w-10 flex justify-center items-center font-poppins leading-5 border hover:bg-none duration-400 hover:border-borderColor hover:text-borderColor',
                  currentPage === sortedCart.length
                    ? 'bg-none border-borderColor text-borderColor'
                    : 'bg-gradient-to-r from-grStart to-grEnd font-poppins border-transparent text-font',
                ].join(' ')}
                onClick={turnNextPage}>
                <ChevronRight />
              </button>
              <button
                className={[
                  'p-1 rounded-full h-10 w-10 flex justify-center items-center font-poppins leading-5 border hover:bg-none duration-400 hover:border-borderColor hover:text-borderColor',
                  currentPage === sortedCart.length
                    ? 'bg-none border-borderColor text-borderColor'
                    : 'bg-gradient-to-r from-grStart to-grEnd font-poppins border-transparent text-font',
                ].join(' ')}
                onClick={swipeEndPage}>
                <ChevronDoubleRight />
              </button>
            </div>
            {cart.length && (
              <div className="mt-10 flex flex-col items-end">
                <span className="font-crimson text-lg leading-6 flex tracking-wider text-font">
                  Total goods to buy: {count}
                </span>
                <hr className="h-01 w-72 smart:w-96 border-none bg-gradient-to-l from-grStart to-grEnd" />
                <span className="font-crimson text-lg leading-6 flex tracking-wider text-font">
                  Total to pay: £{sum}
                </span>
                <div className="flex gap-10">
                  <button
                    className="p-1 mt-4 rounded-xl h-10 w-28 flex justify-center items-center bg-like font-poppins leading-5 border border-transparent text-font hover:bg-transparent hover:border-like hover:text-like duration-300"
                    onClick={() => setOpenModal(true)}>
                    Clear Order
                  </button>
                  <button className="p-1 mt-4 rounded-xl h-10 w-28 flex justify-center items-center bg-gradient-to-r from-grStart to-grEnd font-poppins leading-5 border border-transparent text-font hover:bg-none hover:border-borderColor hover:text-borderColor duration-300">
                    Finish Order
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {isModalOpened && (
        <ModalWindow closeModal={() => setOpenModal(false)}>
          <div className="mt-10 flex flex-col gap-5 justify-center items-center p-3 smart:p-5 ">
            <h2 className="font-crimson font-bold text-lg md:text-xl leading-6 flex tracking-wider text-center">
              Are you sure you want to clear your chest?
            </h2>
            <div className="flex gap-4 justify-center items-center">
              <button
                className="h-12 w-28 font-crimson font-normal text-lg leading-8 tracking-wider flex justify-center items-center duration-500 bg-transparent border-2 rounded-3xl border-like text-like hover:text-font hover:bg-like"
                onClick={handleClearChest}>
                Yep
              </button>
              <button
                className="h-12 w-28 font-crimson font-normal text-lg leading-8 tracking-wider flex justify-center items-center duration-500 bg-transparent border-2 rounded-3xl border-borderColor hover:text-font hover:bg-borderColor"
                onClick={() => setOpenModal(false)}>
                Nope
              </button>
            </div>
          </div>
        </ModalWindow>
      )}
    </section>
  );
};
